import React, { useCallback, useState, useEffect } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, CircleCheck } from "lucide-react";

import TaskEmployees from "../TaskEmployees";
import TaskPriority from "../TaskPriority";
import TaskTimer from "../TaskTimer";
import SmartDatePicker from "../SmartDatePicker";

const formatMySQLDate = (date) => {
    const d = new Date(date);

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = "00";

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const TaskItem = React.memo(({
    task,
    status,
    toggleTaskSelection: toggleTask,
    selectedTasks: selectedTasksProp,
    activeProjectId,
    setCurrentTask,
    setRunningTaskId,
    setEditData,
    setShowDescriptionPopup,
    setSelectedTasks,
    employees,
    currentUser,
    updateProject,
    setToastMessage,
    setToastType,
    isBelow1280,
    nowIST
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id: task._id || task.id });
    const [openDateId, setOpenDateId] = useState(null);
    const [pendingTask, setPendingTask] = useState(null);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const handleTaskClick = useCallback(() => {
        const resolvedProjectId =
            task.projectId || activeProjectId || task?.project?._id || null;

        setPendingTask({
            ...task,
            projectId: resolvedProjectId,
        });

        setRunningTaskId(task._id || task.id);
    }, [task, activeProjectId]);

    useEffect(() => {
        if (!pendingTask) return;

        setCurrentTask(pendingTask);

        setEditData({
            title: pendingTask.title || "",
            description: pendingTask.description?.length
                ? pendingTask.description
                : [{
                    storeLink: "",
                    referenceLink: "",
                    referenceLinkEnabled: false,
                    figmaLink: "",
                    figmaLinkDisabled: false,
                    taskdescription: [],
                    subdescription: "",
                    files: [],
                }],
            priority: pendingTask.priority || "",
            status: pendingTask.status || "",
            dueDate: pendingTask.dueDate ? new Date(pendingTask.dueDate) : null,
            assignedEmployees:
                pendingTask.assignedEmployees?.map(
                    (e) => e.userId || e.employeeId || e.id
                ) || [],
            comments: pendingTask.comments || [],
            timeline: pendingTask.timeline || [],
        });

        setShowDescriptionPopup(true);
    }, [pendingTask]);

    const handleSingleAssigneeUpdate = useCallback(
        async (task, newAssignees = []) => {
            if (!task || !(task._id || task.id)) return;

            const resolvedProjectId =
                activeProjectId ||
                task?.projectId ||
                task?.project?._id;

            if (!resolvedProjectId) return;

            const currentUserName =
                currentUser?.fullName ||
                currentUser?.firstName ||
                currentUser?.username ||
                "Unknown";

            const createdByTask = {
                userId: currentUser?.userId || "Unknown",
                fullName: currentUserName,
                email: currentUser?.email || "info@hubsyntax.com",
            };

            const getEmployeeInfo = (id) =>
                employees.find((e) => e.employeeId === id);

            try {
                const finalIds = Array.isArray(newAssignees)
                    ? newAssignees
                    : [];

                let comments = [];

                if (finalIds.length) {
                    const assignedNames = finalIds
                        .map((id) => getEmployeeInfo(id)?.firstName)
                        .filter(Boolean)
                        .join(", ");

                    comments.push({
                        text: `${currentUserName} assigned to: ${assignedNames}`,
                        createdBy: currentUserName,
                        createdAt: nowIST,
                    });
                }

                const payload = {
                    id: resolvedProjectId,
                    subProjectId: task.subProjectId || null,
                    taskId: task._id || task.id,
                    taskTitle: task.title,
                    status: task.status || "pending",
                    priority: task.priority || "",
                    dueDate: task.dueDate || null,
                    assignedEmployees: finalIds,
                    comments,
                    createdByTask,
                };

                await updateProject.mutateAsync(payload);

                setToastMessage("Assignee updated successfully");
                setToastType("success");
            } catch (error) {
                console.error(error);
                setToastMessage("Error updating assignee");
                setToastType("error");
            }
        },
        [currentUser, employees, updateProject, activeProjectId]
    );

    const handleSinglePriorityUpdate = useCallback(
        async (task, newPriority) => {
            if (!task || !(task._id || task.id)) return;

            const resolvedProjectId =
                activeProjectId ||
                task?.projectId ||
                task?.project?._id;

            if (!resolvedProjectId) return;

            const currentUserName =
                currentUser?.fullName ||
                currentUser?.firstName ||
                currentUser?.username ||
                "Unknown";

            const createdByTask = {
                userId: currentUser?.userId || "Unknown",
                fullName: currentUserName,
                email: currentUser?.email || "info@hubsyntax.com",
            };

            try {
                const comments = newPriority
                    ? [
                        {
                            text: `${currentUserName} changed priority to: ${newPriority}`,
                            createdBy: currentUserName,
                            createdAt: nowIST,
                        },
                    ]
                    : [];

                const payload = {
                    id: resolvedProjectId,
                    subProjectId: task.subProjectId || null,
                    taskId: task._id || task.id,
                    taskTitle: task.title,
                    status: task.status || "pending",
                    priority: newPriority,
                    comments,
                    createdByTask,
                };

                await updateProject.mutateAsync(payload);

                setToastMessage("Priority updated successfully");
                setToastType("success");

            } catch (err) {
                setToastMessage("Error updating priority");
                setToastType("error");
            }
        },
        [currentUser, updateProject, activeProjectId]
    );

    const truncateResponsive = (text) => {
        if (!text) return "";
        const limit = isBelow1280 ? 15 : 40;
        return text.length > limit ? text.slice(0, limit) + "..." : text;
    };

    const handleSingleDueDateUpdate = useCallback(
        async (task, newDueDate) => {
            if (!task || !(task._id || task.id)) return;

            const resolvedProjectId =
                activeProjectId ||
                task?.projectId ||
                task?.project?._id;

            if (!resolvedProjectId) return;

            const currentUserName =
                currentUser?.fullName ||
                currentUser?.firstName ||
                currentUser?.username ||
                "Unknown";

            const createdByTask = {
                userId: currentUser?.userId || "Unknown",
                fullName: currentUserName,
                email: currentUser?.email || "info@hubsyntax.com",
            };

            try {
                const formattedDate = newDueDate
                    ? formatMySQLDate(newDueDate)
                    : "cleared";

                const comments = [
                    {
                        text: `${currentUserName} changed due date to: ${formattedDate}`,
                        createdBy: currentUserName,
                        createdAt: nowIST,
                    },
                ];

                const payload = {
                    id: resolvedProjectId,
                    subProjectId: task.subProjectId || null,
                    taskId: task._id || task.id,
                    taskTitle: task.title,
                    status: task.status || "pending",
                    priority: task.priority || "",
                    dueDate: newDueDate
                        ? formatMySQLDate(newDueDate)
                        : null,
                    comments,
                    createdByTask,
                };

                await updateProject.mutateAsync(payload);

                setToastMessage("Due date updated successfully");
                setToastType("success");
            } catch (err) {
                setToastMessage("Error updating due date");
                setToastType("error");
            }
        },
        [currentUser, updateProject, activeProjectId]
    );

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            className="group flex items-center bg-white px-1 sm:px-3 py-1 sm:py-2 rounded-md shadow-sm hover:bg-gray-50 cursor-pointer mb-2" >
            <div
                className="flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                {...listeners}
            >
                <GripVertical size={16} className="text-gray-500" />
            </div>

            <div className="w-[30px] flex items-center justify-center">
                <input
                    type="checkbox"
                    className={`
            cursor-pointer transition-opacity
            ${selectedTasksProp[status]?.includes(task._id)
                            ? "opacity-100"
                            : "opacity-0 group-hover:opacity-100"
                        }
                   `}
                    checked={selectedTasksProp[status]?.includes(task._id)}
                    onChange={() => toggleTask(status, task._id || task.id)}
                />
            </div>
            <div className=" hidden rs:flex items-center justify-center rounded-full bg-[#299764] text-white mr-[10px]">
                <CircleCheck size={16} />
            </div>
            <div
                className="
    flex-[2] min-w-[100px] sm:min-w-[150px]
    overflow-hidden text-ellipsis whitespace-nowrap
    text-[11px] sm:text-[14px]
    text-gray-800
    hover:text-blue-700 hover:underline
    transition-colors duration-200
  "
                title={task.taskTitle}
                onClick={handleTaskClick}
            >
                <span className="inline">
                    {truncateResponsive(task.title)}
                </span>
            </div>

            <div
                className="flex-1"
                onClick={(e) => e.stopPropagation()}
            >
                <TaskEmployees
                    selected={task.assignedEmployees?.map(
                        e => e.employeeId || e.userId || e._id
                    ) || []}
                    employees={employees}
                    onChange={(newAssignees) =>
                        handleSingleAssigneeUpdate(task, newAssignees)
                    }
                    placement={isBelow1280 ? "top" : "bottom"}
                    align={isBelow1280 ? "right" : "left"}
                />
            </div>

            <div
                className="hidden xs:block flex-1 text-[11px] sm:text-[14px] text-gray-600"
                onClick={(e) => e.stopPropagation()}
            >
                <SmartDatePicker
                    placement="bottom"
                    align="right"
                    open={openDateId === task._id}
                    setOpen={(isOpen) =>
                        setOpenDateId(isOpen ? task._id : null)
                    }
                    selected={task.dueDate || null}
                    setSelected={(date) => {
                        const finalDate = date ? new Date(date) : null;
                        handleSingleDueDateUpdate(task, finalDate);
                    }}
                />
            </div>

            <div className="flex-1">
                <TaskPriority
                    value={task.priority}
                    onChange={(newPriority) =>
                        handleSinglePriorityUpdate(task, newPriority)
                    }
                    placement={window.innerWidth <= 480 ? "top" : "bottom"}
                    align={window.innerWidth <= 480 ? "right" : "left"}
                />
            </div>
            <div className="hidden xs:flex flex-1 text-[14px] text-gray-600  items-center gap-1">
                <TaskTimer timeline={task.timeline}
                />
            </div>
        </div>
    );
});

export default TaskItem;
